/*
 * This component is largely similar to OneTimePasswordStep.tsx, but is used specifically during the sign in flow
 * if we detect the user has not confirmed their account yet.
 */
import React, { useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { H2 } from '../forms';
import { Body2, Button, TextButton } from '../../components/common';
import { useMessageGetter } from 'react-message-context';
import { InputField } from '../../components/common/forms';
import { ButtonContainer, Form, FormContainer } from '../layoutComponents';
import FormError from '../../components/common/forms/FormError';
import { OneTimePasswordFormState, oneTimePasswordSchema } from './schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { requestConfirmationCode, submitRegistrationConfirmationCode } from '../../apis_kenno';
import { NotificationType, useAppDispatch } from '../../context/appContext';
import { showNotification } from '../../context/appActions';

type Props = {
  username: string;
  onSuccess: () => void;
  onCancel: () => void;
};

const SignInOneTimePassword: React.FC<Props> = ({ username, onSuccess, onCancel }) => {
  const translationPrefix = 'signin.oneTimePassword';
  const appDispatch = useAppDispatch();
  const t = useMessageGetter(translationPrefix);
  const [stepFailed, setStepFailed] = useState(false);
  const [submitIsBusy, setSubmitIsBusy] = useState(false);
  const [newCodeIsBusy, setNewCodeIsBusy] = useState(false);
  const formMethods = useForm<OneTimePasswordFormState>({ resolver: zodResolver(oneTimePasswordSchema) });

  const onSubmit: SubmitHandler<OneTimePasswordFormState> = async (data) => {
    setStepFailed(false);
    setSubmitIsBusy(true);
    const { code } = data;
    try {
      await submitRegistrationConfirmationCode(code, username);
      setStepFailed(false);
      onSuccess();
    } catch (error) {
      setStepFailed(true);
    } finally {
      setSubmitIsBusy(false);
    }
  };

  const onRequestNewCode = async () => {
    setNewCodeIsBusy(true);
    try {
      await requestConfirmationCode(username);
      showNotification(appDispatch, t('codeSent'), NotificationType.INFO);
    } catch (e) {
      showNotification(appDispatch, t('codeSendFailed'), NotificationType.ERROR);
    } finally {
      setNewCodeIsBusy(false);
    }
  };

  return (
    <FormContainer>
      <FormProvider {...formMethods}>
        <H2>{t('title')}</H2>
        <Body2>
          {t('instructions')}
          <TextButton disabled={newCodeIsBusy} onClick={onRequestNewCode}>
            {t('instructionsLink')}
          </TextButton>
        </Body2>

        <Form onSubmit={formMethods.handleSubmit(onSubmit)}>
          <InputField
            fieldPath="code"
            placeholder={t('code')}
            translationPrefix={translationPrefix}
            type="text"
            allowDefaultSubmit
            autoComplete="one-time-code"
            required
            dark
          />
          {stepFailed && <FormError message={t('generalError')} />}
          <ButtonContainer>
            <Button type="submit" id="submit" primary disabled={submitIsBusy}>
              {t('submit')}
            </Button>
            <Button onClick={onCancel}>{t('cancel')}</Button>
          </ButtonContainer>
        </Form>
      </FormProvider>
    </FormContainer>
  );
};

export default SignInOneTimePassword;
